'use client'

import { useLocale } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'
import type { CkEditorRendererProps } from '@/components/elementary/CkEditorRenderer/types'
import { ckEditorTextVariants } from '@/lib/constants'
import { cn } from '@/lib/styles'
import { CK_EDITOR_TEST_IDS } from '@/lib/test-ids'
import { processIframes, processImages, processLinks, processTagNames } from '@/lib/toc-tree'
import type { AppLocale } from '@/types/general'

/**
 * Use this variant of the CkEditorRenderer whenever you are within the client context. That means you will need to validate that the entire
 * tree in which this component is being rendered.
 */
const CkEditorCSRRendererInner = ({
	htmlContent,
	className,
	variant = 'default',
	context = 'default',
	defaultTypography = 'default',
}: CkEditorRendererProps) => {
	const [isMounted, setIsMounted] = useState(false)
	const locale = useLocale() as AppLocale
	const usedIds = new Set<string>()
	useEffect(() => {
		setIsMounted(true)
	}, [])

	const processedHtmlContent = useMemo(() => {
		if (!isMounted) {
			return undefined
		}

		if (!htmlContent) return ''

		const parser = new DOMParser()
		const doc = parser.parseFromString(htmlContent, 'text/html')

		// Process links
		processLinks(doc, locale)
		// Process images
		processImages(doc)
		// Process iframes
		processIframes(doc)
		// Process headings and paragraphs
		processTagNames(doc, usedIds)

		return doc.body.innerHTML
	}, [htmlContent, locale, usedIds, isMounted])

	return processedHtmlContent ? (
		<div className="ck-content" data-test-id={CK_EDITOR_TEST_IDS.COMPONENT}>
			<div
				className={cn(
					`ck-editor-${variant}`,
					`ck-editor-ctxt-${context}`,
					`ck-editor-typography-${defaultTypography !== 'default' ? ckEditorTextVariants?.[defaultTypography] : defaultTypography}`,
					className,
				)}
				// biome-ignore lint/security/noDangerouslySetInnerHtml: Content is coming from Strapi, i.e. from employees, not users
				dangerouslySetInnerHTML={{
					__html: processedHtmlContent,
				}}
			/>
		</div>
	) : null
}

export default CkEditorCSRRendererInner
